import React, { useState, useEffect } from "react";
import {
  fetchpendingsubmissionCandidates,
  restoreCandidate,
  softDeleteCandidate,
} from "../../services/user/api";

import { RotateCcw, Trash2, Search } from "lucide-react";

import "../../styles/user/pendingSubmission.css";

const PendingSubmissionPage = () => {
  const [candidates, setCandidates] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");

  const [modal, setModal] = useState({
    visible: false,
    type: "",
    message: "",
    action: null,
  });
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);

  const [sortField, setSortField] = useState("createddate");
  const [sortOrder, setSortOrder] = useState("desc");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);


  useEffect(() => {
    loadCandidates();
  }, [currentPage, entriesPerPage, debouncedSearch, sortField, sortOrder])

  const loadCandidates = async () => {
    try {
      setLoading(true);

      const response = await fetchpendingsubmissionCandidates(
        currentPage,
        entriesPerPage,
        debouncedSearch,
        sortField || "createddate",
        sortOrder
      );
      const data = response.data.data;

      setCandidates(data?.content || []);
      setTotalElements(data?.totalElements || 0);
    } catch (error) {
      setFetchError("An error occurred while fetching data.");
      setCandidates([]);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  };

  const confirmAction = (type, candidateId) => {
    setSelectedCandidateId(candidateId);
    const message =
      type === "restore"
        ? "Are you sure you want to extend this candidate's login period?"
        : "Are you sure you want to delete this candidate?";

    setModal({
      visible: true,
      type,
      message,
      action: () =>
        type === "restore"
          ? handleRestore(candidateId)
          : handleSoftDelete(candidateId),
    });
  };

  const handleRestore = async (candidateId) => {
    try {
      const res = await restoreCandidate(candidateId);
      setModal({
        visible: true,
        type: "message",
        message: res.data?.message || "Login period extended and email sent.",
        action: null,
      });
      loadCandidates();
    } catch (error) {
      setModal({
        visible: true,
        type: "message",
        message: "Failed to restore candidate.",
        action: null,
      });
    }
  };

  const handleSoftDelete = async (candidateId) => {
    try {
      await softDeleteCandidate(candidateId);

      setCandidates((prev) =>
        prev.filter((c) => c.candidateid !== candidateId)
      );

      setModal({
        visible: true,
        type: "message",
        message: "Candidate deleted successfully.",
        action: null,
      });
    } catch (error) {
      setModal({
        visible: true,
        type: "message",
        message: "Failed to delete candidate.",
        action: null,
      });
    }
  };

  const closeModal = () => {
    setModal({ visible: false, type: "", message: "", action: null });
    setSelectedCandidateId(null);
  };

  const formatDate = (date) => {
    if (!date) return "---";

    const d = new Date(date);

    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();

    return `${day}-${month}-${year}`;
  };


  const totalPages = Math.ceil(totalElements / entriesPerPage);


  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
    setCurrentPage(0);
  };

 const sortSymbol = (field) => {
  return (
    <span className="sort-icons">
      <span
        className={`arrow ${
          sortField === field && sortOrder === 'asc' ? 'active' : ''
        }`}
      >
        ↑
      </span>
      <span
        className={`arrow ${
          sortField === field && sortOrder === 'desc' ? 'active' : ''
        }`}
      >
        ↓
      </span>
    </span>
  );
};


  return (
    <>
      <p className="client-header">Pending Submission Candidates</p>

      <div className="client-wrapper">
        {/* Modal */}
        {modal.visible && (
          <div className="modal-overlay">
            <div className="modal-content">
              <p>{modal.message}</p>
              {modal.action ? (
                <div className="modal-actions">

                  <button className="modal-btn no" onClick={closeModal}>
                    No
                  </button>
                  <button
                    className="modal-btn confirm"
                    onClick={() => {
                      modal.action();
                      closeModal();
                    }}
                  >
                    Yes
                  </button>
                </div>
              ) : (
                <div className="modal-actions">
                  <button className="modal-btn ok" onClick={closeModal}>
                    OK
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="clinet-table-controls">
          <div className="entries-select">
            Show{" "}
            <select
              value={entriesPerPage}
              onChange={(e) => {
                setEntriesPerPage(Number(e.target.value));
                setCurrentPage(0);
              }}
            >
              <option value="10">10</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>{" "}
            entries
          </div>


          <div className="client-search-box">
            <div className="client-search-input-wrapper">
              <input
                id="client-search-input"
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(0);
                }}
                placeholder="Search by any field..."
              />
              <Search className="client-search-icon" size={18} />
            </div>
          </div>
        </div>

        <div className="clinet-table-container">
          <table className="candidates-clinet-table">
            <colgroup>
              <col style={{ width: "130px" }} />
              <col style={{ width: "180px" }} />
              <col style={{ width: "220px" }} />
              <col style={{ width: "150px" }} />
              <col style={{ width: "120px" }} />
              <col style={{ width: "140px" }} />
              <col style={{ width: "130px" }} />
              <col style={{ width: "200px" }} />
            </colgroup>
            <thead>
              <tr>
                <th onClick={() => handleSort("candidateid")}>
                  Candidate ID {sortSymbol("candidateid")}
                </th>
                <th onClick={() => handleSort("fullname")}>
                  Name {sortSymbol("fullname")}
                </th>
                <th onClick={() => handleSort("email")}>
                  Email {sortSymbol("email")}
                </th>
                <th onClick={() => handleSort("mobileNumber")}>
                  Mobile Number {sortSymbol("mobileNumber")}
                </th>
                <th onClick={() => handleSort("dateOfBirth")}>
                  DOB {sortSymbol("dateOfBirth")}
                </th>
                <th onClick={() => handleSort("caseInitiatedDateOnlyAsString")}>
                  Created Date {sortSymbol("caseInitiatedDateOnlyAsString")}
                </th>
                 <th onClick={() => handleSort("caseInitiatedTimeOnly")}>
                    Created Time {sortSymbol("caseInitiatedTimeOnly")}
                  </th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="loading">
                    Loading Candidates...
                  </td>
                </tr>
              ) : fetchError ? (
                <tr>
                  <td colSpan="8" className="no-data">
                    {fetchError}
                  </td>
                </tr>
              ) : candidates.length > 0 ? (
                candidates.map((candidate) => (
                  <tr key={candidate.candidateid}>
                    <td>{candidate.candidateid}</td>
                    <td>{candidate.fullname}</td>
                    <td>{candidate.email}</td>
                    <td>{candidate.mobileNumber}</td>
                    <td>{candidate.dateOfBirth}</td>
                    <td>{formatDate(candidate.caseInitiatedDateOnly)}</td>
                    <td>{candidate.caseInitiatedTimeOnly}</td>
                    <td>
                      <span
                        className="action-icon recycle"
                        title="Restore Login"
                        style={{ color: "#6b21a8", cursor: "pointer" }}
                        onClick={() =>
                          confirmAction("restore", candidate.candidateid)
                        }
                      >
                        <RotateCcw size={20} />
                      </span>

                      {candidate.loginExpired ? (
                        <span className="login-expired-badge">
                          Login Expired
                        </span>
                      ) : (
                        <span className="active-badge">Active</span>
                      )}

                      <span
                        className="action-icon delete"
                        title="Delete"
                        style={{
                          color: "#dc2626",
                          cursor: "pointer",
                          marginLeft: "12px",
                        }}
                        onClick={() =>
                          confirmAction("delete", candidate.candidateid)
                        }
                      >
                        <Trash2 size={20} />
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="no-data">
                    No matching candidates found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="pagination">
          {totalElements > 0 && (
            <div className="pagination-summary">
              Showing {currentPage * entriesPerPage + 1} to{" "}
              {Math.min((currentPage + 1) * entriesPerPage, totalElements)} of{" "}
              {totalElements} entries
            </div>
          )}

          {totalElements > 0 && (
            <div className="pagination-buttons-user">
              <button
                className="page-btn"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
                disabled={currentPage === 0}
              >
                ←
              </button>

              <button
                className={`page-btn ${"active"}`}
                onClick={() => setCurrentPage(currentPage)}
              >
                {currentPage + 1}
              </button>

              {currentPage + 1 < totalPages && (
                <button
                  className="page-btn"
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  {currentPage + 2}
                </button>
              )}

              <button
                className="page-btn"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))
                }
                disabled={currentPage === totalPages - 1}
              >
                →
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PendingSubmissionPage;
